import cloneDeep from 'lodash/cloneDeep';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

interface DbSyncConfigFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;
    document: any;
    form: any;
    schema: any;
    meta: any;
    schemaOptions: any[];
    connectionConfigured: boolean;
}

const state: DbSyncConfigFormState = {
    loading: false,
    loaded: false,
    fatalError: false,
    document: null,
    form: null,
    schema: null,
    meta: {},
    schemaOptions: [],
    connectionConfigured: false
};

const initState = cloneDeep(state);

export const CONFIG_DATA = 'DB_SYNC_CONFIG_DATA';
export const CONFIG_SCHEMA = 'DB_SYNC_CONFIG_SCHEMA';
export const SCHEMAS_LIST = 'DB_SYNC_SCHEMAS_LIST';
export const CONNECTION_SETTINGS = 'DB_SYNC_CONNECTION_SETTINGS';

export const actions: ActionTree<DbSyncConfigFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE');
    },

    init({ dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit, dispatch }, params) {
        const vm: any = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const queries: any[] = [
                vm.$q(CONFIG_SCHEMA).raw('schemas.get').action({ id: 's:db_sync_configs' }),
                vm.$q(SCHEMAS_LIST).raw('db_sync.schemas_list').action({}),
                vm.$q(CONNECTION_SETTINGS)
                    .raw('documents.list')
                    .action({
                        columns: {
                            name: 'st.name',
                            value: 'st.value'
                        },
                        from: 's:settings',
                        from_as: 'st',
                        page_size: 200,
                        order: [{ $asc: ['st.name'] }]
                    })
            ];

            if (params.documentId) {
                queries.unshift(
                    vm.$q(CONFIG_DATA).raw('documents.get').action({ id: params.documentId })
                );
            }

            vm.$db(queries)
                .then((result: ServerResult) => {
                    const schemaOp = result.getOperation(CONFIG_SCHEMA);
                    if (schemaOp.hasError()) {
                        commit('LOADED_ERROR');
                        reject(schemaOp.getContent());
                        return;
                    }

                    const schemasOp = result.getOperation(SCHEMAS_LIST);
                    if (schemasOp.hasError()) {
                        commit('LOADED_ERROR');
                        reject(schemasOp.getContent());
                        return;
                    }

                    const settingsOp = result.getOperation(CONNECTION_SETTINGS);
                    if (settingsOp.hasError()) {
                        commit('LOADED_ERROR');
                        reject(settingsOp.getContent());
                        return;
                    }

                    const schemaContent = schemaOp.getContent();
                    commit('SET_SCHEMA', schemaContent.schema);

                    const schemas = schemasOp.getContent().documents || [];
                    commit('SET_SCHEMA_OPTIONS', schemas);

                    const settings = settingsOp.getContent().documents || [];
                    commit('SET_CONNECTION_STATUS', settings);

                    if (params.documentId) {
                        const docContent = result.getOperation(CONFIG_DATA).getContent();
                        commit('SET_FORM_DATA', docContent);
                    } else {
                        commit('GENERATE_FORM_DATA', {
                            schema: schemaContent.schema,
                            initParams: {
                                direction: 'pull',
                                sync_strategy: 'full_replace',
                                enabled: true,
                                interval_minutes: 15,
                                last_status: 'never'
                            }
                        });
                    }

                    commit('LOADED_SUCCESS');
                    resolve(null);
                })
                .catch(() => {
                    commit('LOADED_ERROR');
                    reject();
                });
        });
    },

    saveForm({ commit }, documentId) {
        const vm: any = (this as any)._vm;
        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const saveQuery = documentId
                ? vm.$q('DB_SYNC_CONFIG_SAVE').update(documentId, normalizedForm)
                : vm.$q('DB_SYNC_CONFIG_SAVE').create('s:db_sync_configs', normalizedForm);

            vm.$db([saveQuery])
                .then((result: ServerResult) => {
                    const operationResult = result.getOperation('DB_SYNC_CONFIG_SAVE');
                    if (operationResult.hasError()) {
                        commit('LOADED_SUCCESS');
                        reject();
                        return;
                    }

                    commit('ON_FORM_SAVE', operationResult.getContent());
                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                })
                .catch(reject);
        });
    },

    changeFormValue({ commit }, { field, newValue }) {
        commit('CHANGE_VALUE', { field, newValue });
    }
};

export const mutations: MutationTree<DbSyncConfigFormState> = {
    RESET_STATE(state) {
        Object.assign(state, cloneDeep(initState));
    },

    START_LOADING(state) {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS(state) {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR(state) {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_SCHEMA(state, schema) {
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
    },

    SET_SCHEMA_OPTIONS(state, schemas) {
        state.schemaOptions = schemas || [];
    },

    SET_CONNECTION_STATUS(state, settings) {
        const dbSyncSettings = (settings || []).filter(
            (row: any) => row.name && String(row.name).startsWith('db_sync.')
        );

        const byName = dbSyncSettings.reduce((acc: any, row: any) => {
            acc[row.name] = row.value;
            return acc;
        }, {});

        const hasConnstr = !!(byName['db_sync.dblink_connstr'] && byName['db_sync.dblink_connstr'].trim());
        const hasParts =
            byName['db_sync.remote_host'] &&
            byName['db_sync.remote_dbname'] &&
            byName['db_sync.remote_user'] &&
            byName['db_sync.remote_password'];

        state.connectionConfigured = !!(hasConnstr || hasParts);
    },

    SET_FORM_DATA(state, { document, schema }) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, { schema, initParams }) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, { document, schema }) {
        state.document = document;
        state.schema = schema;
        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state, { field, newValue }) {
        state.form[field] = newValue;
    }
};

export const STORE_KEY = '$_db_sync_config_form';

export const store = {
    namespaced: true,
    state,
    actions,
    mutations
};
